import { useEffect, useRef } from "react";
import { Icons } from "@/components/ui";
import { useT, type Translator } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { uiMessages } from "@/i18n/messages/ui";
import { wallMessages } from "@/i18n/messages/wall";
import type { WallScope } from "@/stores/wall-scope-store";
import { WallChromeButton } from "./wall-chrome-button";
import { useWallScrollRoot } from "./wall-scroll-root";

/** Placeholder card heights, one column each, while the first page loads. */
const SKELETON_HEIGHTS = [260, 180, 320, 220] as const;
/** Start the next page this far before the end of the wall comes into view. */
const LOAD_AHEAD = "0px 0px 600px 0px";

/**
 * Four card-shaped blocks on the wall's own column grid (wrapping when it
 * has fewer columns) while the first page is on its way.
 */
export function WallSkeleton() {
  const t = useT(uiMessages);
  return (
    <div
      className="grid grid-cols-[repeat(var(--wall-cols),minmax(0,1fr))] gap-[var(--wall-gap)]"
      aria-busy="true"
      data-testid="wall-skeleton"
    >
      <span role="status" className="sr-only">
        {t("loading")}
      </span>
      {SKELETON_HEIGHTS.map((height, index) => (
        <div
          key={index}
          className="rounded-card bg-card-surface opacity-40 motion-safe:animate-pulse"
          style={{ height }}
        />
      ))}
    </div>
  );
}

function emptyText(
  t: Translator<typeof wallMessages.en>,
  scope: WallScope,
  searching: boolean,
): string {
  if (searching) {
    return t(
      scope.kind === "all" ? "emptySearchEverything" : "emptySearchProject",
    );
  }
  return t(scope.kind === "archived" ? "emptyArchived" : "emptyWall");
}

/** The wall, Archived or a search with nothing in it. */
export function WallEmptyState({
  scope,
  searching,
}: {
  scope: WallScope;
  searching: boolean;
}) {
  const t = useT(wallMessages);
  return (
    <p className="py-appkit-3xl text-center" data-testid="wall-empty-state">
      {/* The text wears the wall chrome, over the frost or the desktop. */}
      <span className="wall-chrome inline-block rounded-pill px-4 py-2 text-appkit-subhead text-secondary">
        {emptyText(t, scope, searching)}
      </span>
    </p>
  );
}

/** A page of the wall, Archived or a search did not load. */
export function WallFeedError({ onRetry }: { onRetry: () => void }) {
  const t = useT(wallMessages);
  const tCanvas = useT(canvasMessages);
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-appkit-md py-appkit-3xl text-center"
      data-testid="wall-feed-error"
    >
      <p className="wall-chrome rounded-pill px-4 py-2 text-appkit-subhead text-secondary">
        {t("cardsDidNotLoad")}
      </p>
      <WallChromeButton onClick={onRetry} data-testid="wall-feed-retry">
        {tCanvas("retry")}
      </WallChromeButton>
    </div>
  );
}

/**
 * The end of the wall: loads the next page as it comes near the view, and
 * leaves room under the last cards for the input bar.
 */
export function WallFeedEnd({
  active,
  loaded,
  onReach,
}: {
  /** False while the wall is hidden or the feed has no more pages. */
  active: boolean;
  /** Items loaded so far: after a page lands, a visible end loads again. */
  loaded: number;
  onReach: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const rootRef = useWallScrollRoot();
  const onReachRef = useRef(onReach);
  useEffect(() => {
    onReachRef.current = onReach;
  }, [onReach]);

  useEffect(() => {
    const element = ref.current;
    if (!active || !element || typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onReachRef.current();
        }
      },
      { root: rootRef?.current ?? null, rootMargin: LOAD_AHEAD },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [active, loaded, rootRef]);

  return <div ref={ref} className="h-40" data-testid="wall-feed-end" />;
}

/**
 * New work above the view while the wall is scrolled down (Part 04 Q5),
 * centred under the header's search field (mockup `.newp`).
 */
export function WallNewPill({
  count,
  onShow,
}: {
  count: number;
  onShow: () => void;
}) {
  const t = useT(wallMessages);
  if (count === 0) return null;
  return (
    <div className="pointer-events-none absolute inset-x-0 top-[calc(100%+12px)] z-[2] flex justify-center">
      <WallChromeButton
        className="pointer-events-auto"
        onClick={onShow}
        data-testid="wall-new-pill"
      >
        <Icons.ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
        {t("newCount", { count })}
      </WallChromeButton>
    </div>
  );
}
